import { call, put, select, takeLatest } from 'redux-saga/effects'

import { IHateoasLink } from '@store'
import { selectAuthToken } from '@store/auth/selectors'
import { fetchCampaign, fetchCampaignSuccess, selectCampaignLink } from '@store/campaigns'
import {
  attachTranscript,
  attachTranscriptFailure,
  attachTranscriptSuccess,
} from '@store/discoveryRuns/actions'
import {
  ATTACH_TRANSCRIPT_REL,
  buildTranscriptFormData,
  extractVerbatimErrorMessage,
  isAlreadyAttachedError,
  multipartRequest,
} from '@store/discoveryRuns/api'
import { toast } from '@tribegroup'

export function* attachTranscriptSaga(action: ReturnType<typeof attachTranscript>) {
  const { campaignId, file } = action.payload
  try {
    const token: string = yield select(selectAuthToken)
    // attach_transcript is a rel on the BRIEF's own top-level `links` (not
    // nested inside the `sourcing` block) — same selector used for every
    // other campaign-scoped HATEOAS action.
    const link: IHateoasLink | undefined = yield select(
      selectCampaignLink,
      campaignId,
      ATTACH_TRANSCRIPT_REL,
    )
    if (!link) {
      // Surface the dead-end to the user, not just redux state (review fix).
      yield put(attachTranscriptFailure("Transcript can't be attached right now."))
      yield call(toast, "Transcript can't be attached right now.")
      return
    }

    const formData = buildTranscriptFormData(file)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response: any = yield call(multipartRequest, link.method, link.href, formData, token)
    // Success returns the full brief view — refresh the campaign the same
    // way a normal fetchCampaign would, rather than duplicating its shape.
    yield put(fetchCampaignSuccess(response))
    yield put(attachTranscriptSuccess())
  } catch (e: any) {
    // House pattern: 422 (and other) error bodies are surfaced verbatim,
    // not paraphrased — see @store/discoveryRuns/api's extractVerbatimErrorMessage.
    const message = extractVerbatimErrorMessage(e)
    if (isAlreadyAttachedError(e)) {
      // BE state is AHEAD of the FE (transcript already attached server-side)
      // — effective success: refetch the campaign so transcript_attached
      // syncs, clear the in-flight flag, keep the verbatim toast (review fix).
      yield put(fetchCampaign(campaignId))
      yield put(attachTranscriptSuccess())
      yield call(toast, message)
      return
    }
    yield put(attachTranscriptFailure(message))
    yield call(toast, message)
  }
}

export default function* watchAttachTranscript() {
  yield takeLatest(attachTranscript.type, attachTranscriptSaga)
}
